/**
 * Pure view-model for the canonical report (CORE-3). No React / React Native
 * imports so it is unit-tested directly (`__tests__/reportModel.test.ts`).
 */
import { normalizeLevelKey, type LevelKey } from '../../utils/icfLevels';
import type {
  PersistedAnalysisStructured,
  ReportEvidence,
  ReportMentorQuestion,
  ReportPracticeItem,
  ReportStatus,
} from '../../types/report';

export const COMPETENCY_CODES = ['C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7', 'C8'] as const;
export type CompetencyCode = (typeof COMPETENCY_CODES)[number];

export const COMPETENCY_NAME_KEYS: Record<CompetencyCode, string> = {
  C1: 'icfProgress.compEthics',
  C2: 'icfProgress.compMindset',
  C3: 'icfProgress.compAgreements',
  C4: 'icfProgress.compTrust',
  C5: 'icfProgress.compPresence',
  C6: 'icfProgress.compListening',
  C7: 'icfProgress.compAwareness',
  C8: 'icfProgress.compGrowth',
};

/** Real reports carry up to 5 strength quotes and 1 development quote per competency. */
export const MAX_STRENGTH_QUOTES = 5;
export const MAX_DEVELOPMENT_QUOTES = 1;

// ---------------------------------------------------------------------------
// State gate (CORE-4)
// ---------------------------------------------------------------------------

export type ReportGateState =
  | 'report' // render the report
  | 'processing' // analysis still running: poll and show progress
  | 'no_report' // polling budget exhausted, nothing persisted
  | 'failed'
  | 'legacy'; // an old report without a structured payload

/** How often and how long the report screen polls while an analysis runs. */
export const REPORT_POLL_INTERVAL_MS = 5_000;
export const REPORT_POLL_MAX_ATTEMPTS = 24; // ~2 minutes

export function reportGateState(
  status: ReportStatus,
  hasStructured: boolean,
  pollAttempts: number,
): ReportGateState {
  if (hasStructured && (status === 'completed' || status === 'excluded')) return 'report';
  if (status === 'failed') return 'failed';
  if (status === 'legacy' || status === 'completed' || status === 'excluded') return 'legacy';
  // pending / processing / no_report: the pipeline writes the row only when
  // it finishes, so keep polling for a while before giving up.
  return pollAttempts < REPORT_POLL_MAX_ATTEMPTS ? 'processing' : 'no_report';
}

/**
 * The state worth an `app_report_viewed` event: what the user actually saw
 * once loading settled (the report, a failure, a legacy text report or "no
 * report"). Still processing is not a view; the screen polls.
 */
export function reportViewedState(state: ReportGateState | null): Exclude<ReportGateState, 'processing'> | null {
  if (!state || state === 'processing') return null;
  return state;
}

export function shouldPollReport(state: ReportGateState): boolean {
  return state === 'processing';
}

// ---------------------------------------------------------------------------
// Competencies (C1/C2 policy, evidence caps)
// ---------------------------------------------------------------------------

export type CompetencyPolicy = 'observed_only' | 'ethics_concern' | 'exam_only' | null;

export interface CompetencyRow {
  code: CompetencyCode;
  nameKey: string;
  levelKey: LevelKey | null;
  policy: CompetencyPolicy;
  /** Narrative, or the policy note for C1/C2. */
  narrative: string;
  strengths: ReportEvidence[];
  development: ReportEvidence[];
  inconsistentPatterns: string[];
}

export function competencyRows(structured: PersistedAnalysisStructured): CompetencyRow[] {
  const analyses = structured.competency_analyses || {};
  return COMPETENCY_CODES.flatMap((code) => {
    const entry = analyses[code];
    if (!entry) return [];
    // Older payloads still carry C1/C2 in `competency_levels`; the policy
    // lives in `competency_analyses[code].level === ""` + the flags.
    const levelKey = normalizeLevelKey(entry.level);
    let policy: CompetencyPolicy = null;
    let narrative = entry.narrative || '';
    // C1 never shows a level (GRD-R06): the ethics veto shows a concern flag.
    if (code === 'C1') {
      policy = structured.c1_ethics_concern ? 'ethics_concern' : 'observed_only';
    }
    if (code === 'C2' && (structured.c2_transcript_assessed === false || !levelKey)) {
      policy = 'exam_only';
      narrative = structured.c2_note || narrative;
    }
    const verdict = structured.competency_verdicts?.[code];
    return [{
      code,
      nameKey: COMPETENCY_NAME_KEYS[code],
      levelKey: policy ? null : levelKey,
      policy,
      narrative,
      strengths: policy === 'exam_only' ? [] : (entry.positive_examples || []).slice(0, MAX_STRENGTH_QUOTES),
      // The ethics-concern flag keeps the moment that raised it.
      development: policy && policy !== 'ethics_concern'
        ? []
        : (entry.development_examples || []).slice(0, MAX_DEVELOPMENT_QUOTES),
      inconsistentPatterns: policy ? [] : (verdict?.inconsistent_patterns || []),
    }];
  });
}

// ---------------------------------------------------------------------------
// Summary, next steps, development plan
// ---------------------------------------------------------------------------

/** `**bold**` → plain text; the report renders plain paragraphs. */
export function plainText(value: string | null | undefined): string {
  return String(value || '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .trim();
}

/** Split a summary into its lead sentence (bold lead in real reports) and the rest. */
export function splitLead(value: string | null | undefined): { lead: string; rest: string } {
  const raw = String(value || '').trim();
  const bold = raw.match(/^\*\*(.+?)\*\*\s*(.*)$/s);
  if (bold) return { lead: bold[1].trim(), rest: plainText(bold[2]) };
  return { lead: '', rest: plainText(raw) };
}

export interface ReportHeadlineModel {
  /** Badge level for the header; null when a mentee frame replaces it. */
  levelKey: LevelKey | null;
  /** Target-framed mentee headline ("ACC Level: Not Yet Met"), else null. */
  menteeLabel: string | null;
}

/**
 * Header level for a report. Mentee analyses (2026-10-07 target frame) carry
 * `level_decision.mentee_target_level`; their headline names only the
 * engagement target, so the raw level (which, when the target is not met, is
 * the level below it) is never shown as a badge.
 */
export function reportHeadline(structured: PersistedAnalysisStructured | null | undefined): ReportHeadlineModel {
  if (!structured) return { levelKey: null, menteeLabel: null };
  const decision = structured.level_decision || {};
  const menteeTarget = String(decision.mentee_target_level || '').trim();
  const menteeLabel = String(decision.overall_level_label || '').trim();
  if (menteeTarget && menteeLabel) {
    return { levelKey: null, menteeLabel };
  }
  return {
    levelKey: normalizeLevelKey(decision.overall_level || structured.overall_level),
    menteeLabel: null,
  };
}

export interface ReportSummaryModel {
  overallLevelKey: LevelKey | null;
  confidenceLabel: string | null;
  lead: string;
  body: string;
  whyNotHigher: string;
  nextSteps: string[];
  reviewRecommended: boolean;
}

export function reportSummary(structured: PersistedAnalysisStructured): ReportSummaryModel {
  const decision = structured.level_decision || {};
  const { lead, rest } = splitLead(structured.overall_assessment);
  return {
    overallLevelKey: normalizeLevelKey(decision.overall_level || structured.overall_level),
    confidenceLabel: (decision.confidence_label as string) || null,
    lead,
    body: rest,
    whyNotHigher: plainText(decision.why_not_higher as string),
    nextSteps: (structured.recommendations || []).map(plainText).filter(Boolean).slice(0, 3),
    reviewRecommended: Boolean(
      structured.review_recommended || structured.mentor_detailed_report?.level_summary?.review_recommended,
    ),
  };
}

export interface DevelopmentPlanModel {
  practice: ReportPracticeItem[];
  mentorQuestions: ReportMentorQuestion[];
  boundary: string;
}

export function developmentPlan(structured: PersistedAnalysisStructured): DevelopmentPlanModel {
  const detailed = structured.mentor_detailed_report || {};
  return {
    practice: (detailed.next_session_practice_plan || []).filter((item) => item?.practice_focus),
    mentorQuestions: (detailed.live_mentor_session_questions || []).filter((item) => item?.question).slice(0, 3),
    boundary: plainText(detailed.public_boundary),
  };
}
